/** Flight and ordnance modules: a free-flying craft inside a band of the
 * view (`body.flight`), a dropped store that keeps its launcher's momentum and
 * falls out of its elevation (`body.fall`), the blast it makes on the ground
 * (`rule.blast`), an owner-placed marker (`body.reticle`) and the bomb bay
 * that ties them together with an impact-ring lead (`weapon.bomb`).
 *
 * Every point here is an entity CENTRE on the ground plane (`x`, `y − h/2`);
 * elevation (`z`) is height above that plane and never part of a collision. */
import { bool, id, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { approach, clamp, pushVelocity, speedMultiplier } from '../physics.js';
import { addTag, type EntityV1 } from '../world.js';

/** An entity's centre on the ground plane. */
export function centreOf(e: EntityV1): { x: number; y: number } {
  return { x: e.x, y: e.y - e.h / 2 };
}

/** Does the circle (cx, cy, r) touch the entity's box? */
export function circleTouches(e: EntityV1, cx: number, cy: number, r: number): boolean {
  const nx = clamp(cx, e.x - e.w / 2, e.x + e.w / 2), ny = clamp(cy, e.y - e.h, e.y);
  return (cx - nx) ** 2 + (cy - ny) ** 2 <= r * r;
}

export type FlightConfig = {
  speed: number;
  /** Units/s² towards the steered velocity; 0 = instant. */
  accel: number;
  /** The flying band: clearance from each view edge beyond the body. */
  band: { left: number; right: number; top: number; bottom: number };
  /** Mirror the sprite towards horizontal steering (a side-view craft); a
   * pseudo-isometric craft always faces forward. */
  faceMotion: boolean;
  /** Recover from a hit where it is (it publishes its own position as the
   * vitals respawn point) instead of back at the spawn point. */
  recoverInPlace: boolean;
};

/** `body.flight` — a craft steered freely on both axes inside a band of the
 * view (the scroll band of an auto-scroller, a shmup's flying area), at a
 * top speed scaled by the speed protocol (a damaged craft is slower), carried
 * on top of its own steering by the push protocol (a gust band, a current). */
export const flight = defineModule<FlightConfig>({
  id: 'body.flight', version: 1, attach: 'entity', role: 'movement',
  summary: 'Free-flying craft: steers on both axes inside a band of the view (instant or eased), slowed by the speed protocol, optionally recovering from hits in place.',
  config: obj({ speed: num(0.5, 40), accel: num(0, 400),
    band: obj({ left: num(0, 32), right: num(0, 32), top: num(0, 32), bottom: num(0, 32) }), faceMotion: bool(), recoverInPlace: bool() }),
  requires: [], events: [], presentation: {}, proofs: ['steers-both-axes', 'eased-steer', 'held-in-band', 'speed-protocol', 'push-protocol', 'recovers-in-place'],
  animStates: () => ['idle', 'move'],
  check(c, context) {
    const errors: string[] = [];
    if (c.band.left + c.band.right >= context.world.w || c.band.top + c.band.bottom >= context.world.h) errors.push('the band leaves no room to fly');
    return errors;
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, input = e.slot !== null ? ctx.input(e.slot) : null;
      const top = c.speed * speedMultiplier(e);
      const tx = (input?.dx ?? 0) * top, ty = (input?.dy ?? 0) * top;
      e.vx = c.accel === 0 ? tx : approach(e.vx, tx, c.accel * ctx.dt);
      e.vy = c.accel === 0 ? ty : approach(e.vy, ty, c.accel * ctx.dt);
      const { w, h } = ctx.world.bounds;
      const minX = c.band.left + e.w / 2, maxX = w - c.band.right - e.w / 2;
      const minY = c.band.top + e.h, maxY = h - c.band.bottom;
      const push = pushVelocity(ctx.signals('push'), e);
      e.x = clamp(e.x + (e.vx + push.vx) * ctx.dt, minX, maxX);
      e.y = clamp(e.y + (e.vy + push.vy) * ctx.dt, minY, maxY);
      if ((e.x === minX && e.vx < 0) || (e.x === maxX && e.vx > 0)) e.vx = 0;
      if ((e.y === minY && e.vy < 0) || (e.y === maxY && e.vy > 0)) e.vy = 0;
      if (c.faceMotion && input && input.dx !== 0) e.facing = input.dx;
      if (c.recoverInPlace) ctx.signal('checkpoint', e, e, { x: e.x, y: e.y });
    },
    present(ctx, e) {
      if (e && e.status === 'active') ctx.setAnim(e, Math.hypot(e.vx, e.vy) > 0.05 ? 'move' : 'idle');
    },
  },
});

/** `body.fall` — a dropped store (a bomb, a crate): keeps the launch
 * velocity it was given (`params.vx/vy`, its launcher's momentum) and falls
 * from its spawn elevation to the ground over `params.fallTicks` (the
 * launcher owns the fall time), accelerating, carried on the way down by the
 * push protocol (a wind; the bay's impact ring does not lead for it). On
 * touchdown it is tagged `landed` and signals `landed` (where, its tags) for
 * this tick's interaction rules, then is removed on its next move. */
export const fall = defineModule<Record<string, never>>({
  id: 'body.fall', version: 1, attach: 'entity', role: 'movement',
  summary: 'Dropped store: keeps its launch momentum and falls from its elevation to the ground over a fixed time, then signals landed and is removed.',
  config: obj({}),
  spawnParams: obj({ fallTicks: int(1, Number.MAX_SAFE_INTEGER, 'ticks') }),
  requires: [], events: [], presentation: {}, proofs: ['keeps-momentum', 'push-protocol', 'falls-over-fixed-time', 'signals-landed-once', 'removed-after-landing'],
  animStates: () => [],
  init(ctx, e, params) {
    e.vx = typeof params.vx === 'number' ? params.vx : 0;
    e.vy = typeof params.vy === 'number' ? params.vy : 0;
    if (typeof params.fallTicks !== 'number' || params.fallTicks < 1) throw new RangeError('body.fall needs params.fallTicks ≥ 1');
    Object.assign(ctx.data(e), { t: 0, T: params.fallTicks, z0: e.z, landed: false });
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e), T = read(d, 'T');
      if (d.landed === true) { ctx.despawn(e, 'landed'); return; }
      d.t = read(d, 't') + 1;
      const push = pushVelocity(ctx.signals('push'), e);
      e.x += (e.vx + push.vx) * ctx.dt; e.y += (e.vy + push.vy) * ctx.dt;
      const k = Math.min(1, read(d, 't') / T);
      e.z = read(d, 'z0') * (1 - k * k);
      if (k < 1) return;
      e.z = 0; e.vx = 0; e.vy = 0; d.landed = true;
      addTag(e, 'landed');
      const at = centreOf(e);
      ctx.signal('landed', e, e, { x: at.x, y: at.y, tags: [...e.tags] });
    },
  },
});

/** Where a store released now would land, relative to its launcher: it keeps
 * the launcher's velocity `v` for `fallTicks` while the ground moves at `g`
 * (an auto-scrolling view's ground velocity, zero on a fixed screen) — so the
 * ground point it hits is `(v − g)·T` from the launcher. */
export function impactLead(v: { x: number; y: number }, g: { x: number; y: number }, fallTicks: number, dt: number): { x: number; y: number } {
  const t = fallTicks * dt;
  return { x: (v.x - g.x) * t, y: (v.y - g.y) * t };
}

export type BlastConfig = { victimTag: string; radius: number; amount: number };

/** `rule.blast` — when its entity lands (a `landed` signal about it), every
 * victim-tagged entity the blast circle touches takes `amount` damage once,
 * and the entity is spent. Nothing is hit before touchdown. */
export const blast = defineModule<BlastConfig>({
  id: 'rule.blast', version: 1, attach: 'entity', role: 'rule',
  summary: 'Blast on landing: damages every victim-tagged entity within a radius of the touchdown point, once, then the store is spent.',
  config: obj({ victimTag: id(), radius: num(0.1, 8), amount: int(1, 99) }),
  requires: [], events: ['blast'], presentation: { blast: { cue: 'destroy', vfx: { effect: 'shake' } } },
  proofs: ['harmless-in-flight', 'damages-within-radius', 'spares-outside-radius', 'spent-once'],
  animStates: () => [],
  systems: {
    interact(ctx, e) {
      if (!e) return;
      const landed = ctx.signals('landed').find(s => s.target === e.id);
      if (!landed) return;
      const x = landed.data.x as number, y = landed.data.y as number, c = ctx.config;
      let hits = 0;
      for (const v of ctx.entities({ tag: c.victimTag, status: 'active' })) {
        if (v.id === e.id || !circleTouches(v, x, y, c.radius)) continue;
        ctx.signal('damage', v, e, { amount: c.amount, x: v.x, y: v.y - v.h / 2, tags: [...v.tags] });
        hits++;
      }
      ctx.emit('blast', { x, y, hits });
      ctx.despawn(e, 'detonated');
    },
  },
});

/** `body.reticle` — a marker another module positions each tick (an
 * impact ring, an aim point): a `place` signal about it carries the centre
 * `x/y`, `visible` and an optional `alpha`; with no signal it hides. */
export const reticle = defineModule<Record<string, never>>({
  id: 'body.reticle', version: 1, attach: 'entity', role: 'movement',
  summary: 'Owner-placed marker: each tick a place signal positions it (centre, alpha) or it hides.',
  config: obj({}), requires: [], events: [], presentation: {}, proofs: ['follows-place-signal', 'hides-without-signal'],
  includeHidden: true,
  animStates: () => [],
  systems: {
    move(ctx, e) {
      if (!e) return;
      const place = ctx.signals('place').find(s => s.target === e.id);
      if (!place || place.data.visible !== true) { e.status = 'hidden'; return; }
      e.status = 'active';
      e.x = place.data.x as number; e.y = (place.data.y as number) + e.h / 2;
      e.alpha = typeof place.data.alpha === 'number' ? place.data.alpha : 1;
    },
  },
});

export type BombConfig = {
  projectile: string;
  /** The impact-ring marker (a `body.reticle` kind) this bay places. */
  reticle: string;
  trigger: 'a' | 'b';
  cooldownTicks: number;
  /** Ticks a store takes to fall from the craft to the ground. */
  fallTicks: number;
  /** Named scalar holding the stock left (a `rule.resource` mirror); the bay
   * refuses to drop below one. */
  ammoScalar: string;
  /** What the lead is for: the ring locks (full opacity, a `lock` cue) on an
   * entity with this tag, and the solver lines up on them. */
  targetTag: string;
};

type BombCtx = ModuleCtx<BombConfig>;

/** The ground velocity an auto-scrolling view publishes (zero otherwise). */
function groundVelocity(ctx: BombCtx): { x: number; y: number } {
  return { x: ctx.scalar('ground.vx', 0), y: ctx.scalar('ground.vy', 0) };
}

/** `weapon.bomb` — the bomb bay: on a press it drops one store (a `body.fall`
 * kind) carrying the craft's momentum from its elevation, spends one from the
 * stock (a `released` signal with the craft's tags), and shows the release
 * pose. Every tick it places its impact ring where a store released NOW would
 * land — ahead of the craft by the fall's lead — locked when that point is on
 * a target. Its live stores follow the weapon live-shot protocol (`shots`),
 * so a blast credits the seat that dropped it. */
export const bomb = defineModule<BombConfig>({
  id: 'weapon.bomb', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Bomb bay: drops a falling store with the craft’s momentum on a press (stock-limited, cooldown) and keeps an impact ring on the predicted landing point, locked over targets.',
  config: obj({ projectile: id(), reticle: id(), trigger: oneOf('a', 'b'), cooldownTicks: int(1, 300), fallTicks: int(1, 300), ammoScalar: id(), targetTag: id() }),
  requires: ['body.fall', 'body.reticle'], events: ['release', 'lock'],
  presentation: { release: { cue: 'reload' }, lock: { cue: 'click' } },
  proofs: ['drops-on-press', 'keeps-momentum', 'stock-limited', 'cooldown', 'ring-leads-by-fall', 'ring-locks-on-target', 'credits-dropping-seat'],
  animStates: () => ['action-1'],
  mapArchetypeRefs: (c, rename) => ({ ...c, projectile: rename(c.projectile), reticle: rename(c.reticle) }),
  check: (c, context) => [
    ...(context.archetypes[c.projectile]?.modules.includes('body.fall') ? [] : [`${c.projectile} must use body.fall`]),
    ...(context.archetypes[c.reticle]?.modules.includes('body.reticle') ? [] : [`${c.reticle} must use body.reticle`]),
  ],
  init(ctx, e) {
    const ring = ctx.spawn(ctx.config.reticle, { x: e.x, y: e.y, status: 'hidden' });
    Object.assign(ctx.data(e), { cooldown: 0, shots: [], pose: 0, ring: ring.id, locked: false });
  },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      d.shots = (d.shots as string[]).filter(sid => ctx.world.entities[sid] !== undefined);
      if (read(d, 'cooldown') > 0) d.cooldown = read(d, 'cooldown') - 1;
      const ring = ctx.entity(str(d, 'ring'));
      const stocked = ctx.scalar(c.ammoScalar, 0) >= 1;
      if (e.status !== 'active') { if (ring) ctx.signal('place', ring, e, { visible: false }); return; }
      const lead = impactLead({ x: e.vx, y: e.vy }, groundVelocity(ctx), c.fallTicks, ctx.dt);
      const at = centreOf(e), point = { x: at.x + lead.x, y: at.y + lead.y };
      const locked = ctx.entities({ tag: c.targetTag, status: 'active' }).some(t => circleTouches(t, point.x, point.y, 0.2));
      if (locked && d.locked !== true) ctx.emit('lock', { x: point.x, y: point.y, slot: e.slot ?? 0 });
      d.locked = locked;
      if (ring) ctx.signal('place', ring, e, { visible: stocked, x: point.x, y: point.y, alpha: locked ? 1 : 0.55 });
      const input = e.slot !== null ? ctx.input(e.slot) : null;
      const pressed = c.trigger === 'a' ? input?.a === true && !input.prevA : input?.b === true && !input.prevB;
      if (!pressed || !stocked || read(d, 'cooldown') > 0) return;
      const store = ctx.spawn(c.projectile, { x: at.x, y: at.y, z: e.z, facing: e.facing, params: { vx: e.vx, vy: e.vy, fallTicks: c.fallTicks } });
      store.y += store.h / 2; // centred on the craft
      (d.shots as string[]).push(store.id);
      d.cooldown = c.cooldownTicks;
      d.pose = 8; d.fired = true;
      ctx.signal('released', e, e, { tags: [...e.tags] });
      ctx.emit('release', { x: at.x, y: at.y, slot: e.slot ?? 0 });
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (read(d, 'pose') > 0) { ctx.setAnim(e, 'action-1', d.fired === true); d.pose = read(d, 'pose') - 1; }
      d.fired = false;
    },
  },
  // Line up so the ring lands on a target: the craft's spot is the target
  // minus the lead (taken at rest — the rollout decides the release).
  solverTargets(ctx, e, player) {
    if (!e || e.id !== player.id || e.status !== 'active' || ctx.scalar(ctx.config.ammoScalar, 0) < 1) return [];
    const lead = impactLead({ x: 0, y: 0 }, groundVelocity(ctx), ctx.config.fallTicks, ctx.dt);
    return ctx.entities({ tag: ctx.config.targetTag, status: 'active' })
      .map(t => ({ x: t.x - lead.x, y: t.y - t.h / 2 - lead.y + e.h / 2, weight: 5, label: 'bomb-lead', press: true }));
  },
});
